import { useEffect, useRef } from "react";
import { useTransmit } from "~/hooks/use_transmit";

type TransmitChannelOptions = {
  reconnectDelay?: number;
  maxReconnectDelay?: number;
  reconnectBackoffMultiplier?: number;
  lastEventId?: string | null;
  headers?: Record<string, string>;
};

function normalizeChannel(topic: string): string {
  try {
    const parsed = new URL(topic, typeof window !== "undefined" ? window.location.origin : "");
    const isAbsolute = /^https?:\/\//.test(topic);

    if (isAbsolute && parsed.origin === window.location.origin) {
      return parsed.pathname + parsed.search;
    }

    return topic;
  } catch {
    return topic;
  }
}

function normalizePayload<T>(payload: unknown): T {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "topic" in payload &&
    "data" in payload &&
    (payload as Record<string, unknown>).data !== undefined
  ) {
    return (payload as Record<string, unknown>).data as T;
  }

  return payload as T;
}

export function useTransmitChannel<T>(
  topic: string | null,
  onMessage: (data: T) => void,
  options: TransmitChannelOptions = {},
) {
  const { subscribe } = useTransmit();

  // Los handlers llegan casi siempre como arrows inline (nueva identidad en
  // cada render). Suscribirse con esa identidad tumbaba y recreaba la
  // suscripción render a render (el debounce del contexto lo amortigua, pero
  // deja ventanas de carrera y churn). El ref siempre apunta al handler más
  // reciente y el efecto solo depende del topic: una suscripción por topic.
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const headersRef = useRef(options.headers);
  useEffect(() => {
    headersRef.current = options.headers;
  }, [options.headers]);

  useEffect(() => {
    if (!topic) {
      return;
    }

    const channel = normalizeChannel(topic);
    const unsubscribe = subscribe(
      channel,
      (event) => {
        onMessageRef.current(normalizePayload<T>(event));
      },
      { headers: headersRef.current },
    );

    return () => {
      unsubscribe?.();
    };
  }, [topic, subscribe]);

  return {
    connectionState: "connected" as const,
    reconnectAttempts: 0,
    reconnect: () => undefined,
    lastEventId: null as string | null,
    setLastEventId: () => undefined,
  };
}
